import { Timestamp } from 'firebase/firestore';

// 발령 사항 옵션
export const ASSIGNMENT_OPTIONS = [
  '항공사보안책임자',
  '항공사보안감독자', 
  '항공사보안담당자',
  '보안검색감독자',
  '보안검색요원',
  '항공경비감독자',
  '방사선안전관리자',
  '방사선감시기관리책임자',
  '폭발물위협분석관',
  '출입증관리책임자',
  'ACMR',
  '기타보안요원'
] as const;

// 사용자 기록 인터페이스
export interface UserRecord {
  id: string;                           // 문서 ID
  headquarters: string;                 // 본부(부문)
  department: string;                   // 부서
  position: string;                     // 직위
  role: string;                        // 직책
  assignments: string[];               // 발령 사항 (다중 선택)
  location: string;                    // 지점/공항
  photo: {                             // 사진 (이미지 파일)
    url: string | null;
    fileName: string | null;
    uploadedAt: Timestamp | null;
  };
  name: string;                        // 성명
  email: string;                       // 이메일 (로그인용)
  employeeId: string;                  // 사번
  appointmentPeriod: {                 // 발령기간
    start: Timestamp;
    end: Timestamp | null;
  };
  initialEducationCert: {              // 초기 교육이수증 (PDF)
    url: string | null;
    fileName: string | null;
    uploadedAt: Timestamp | null;
  };
  recentEducationCert: {               // 정기 교육이수증 (PDF)
    url: string | null;
    fileName: string | null;
    uploadedAt: Timestamp | null;
  };
  remarks: string;                      // 비고
  initialEducationDate: Timestamp | null;  // 초기 보안교육 이수일
  recentEducationDate: Timestamp | null;   // 정기 보안교육 이수일
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;                    // 생성자 UID
  updatedBy: string;                    // 수정자 UID
}

// 권한 레벨 타입
export type PermissionLevel = 'admin' | 'editor' | 'viewer';

// 사용자 권한 인터페이스
export interface UserPermission {
  email: string;
  name?: string;                        // 사용자 이름 (인원 등록된 경우)
  userId?: string;                      // 인원 등록된 경우 사용자 ID
  level: PermissionLevel;               // 권한 레벨
  canEdit: boolean;                     // 편집 권한 (backward compatibility)
  grantedBy: string;                    // 권한 부여자
  grantedAt: Timestamp;
}

// 앱 설정 인터페이스
export interface AppSettings {
  language: 'ko' | 'en';
  theme: 'light' | 'dark';
}

// 파일 업로드 정보
export interface FileInfo {
  url: string | null;
  fileName: string | null;
  uploadedAt: Timestamp | null;
}

// 발령기간 정보
export interface AppointmentPeriod {
  start: Timestamp;
  end: Timestamp | null;
}

// 사용자 폼 데이터 (입력용)
export interface UserFormData {
  headquarters: string;
  department: string;
  position: string;
  role: string;
  assignments: string[];               // 발령 사항 (다중 선택)
  location: string;                    // 지점/공항
  name: string;
  email: string;                       // 이메일 (로그인용)
  employeeId: string;
  appointmentPeriod: {
    start: Date;
    end: Date | null;
  };
  remarks: string;
  initialEducationDate: Date | null;  // 초기 보안교육 이수일
  recentEducationDate: Date | null;   // 정기 보안교육 이수일
}

// 인증 사용자 정보
export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

// 앱 컨텍스트 상태
export interface AppContextState {
  user: AuthUser | null;
  loading: LoadingState;
  permissions: UserPermission | null;
  settings: AppSettings;
}

// 파일 업로드 타입
export type FileUploadType = 'photo' | 'initialCert' | 'recentCert';

// 검색 및 필터 옵션
export interface SearchFilters {
  search: string;
  headquarters: string;
  department: string;
  position: string;
  assignments: string;                 // 발령 사항 필터
  location: string;                    // 지점/공항 필터
  initialEducationStartDate: string;
  initialEducationEndDate: string;
  recentEducationStartDate: string;
  recentEducationEndDate: string;
}

// 정렬 옵션
export interface SortOption {
  field: keyof UserRecord;
  direction: 'asc' | 'desc';
}

// API 응답 타입
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// 로딩 상태
export interface LoadingState {
  users: boolean;
  upload: boolean;
  delete: boolean;
  auth: boolean;
}

// 에러 상태
export interface ErrorState {
  message: string;
  code?: string;
  timestamp: Date;
}